import { useState, ReactNode, forwardRef, ForwardRefExoticComponent, ComponentProps, CSSProperties } from 'react';
import * as Select from '@radix-ui/react-select';
import { withStyles } from '../../../utils';
import styles from './PulloverSelect.module.scss';
import UtilButton from '../UtilButton/UtilButton';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';

const cn = withStyles(styles);

type PulloverSelectProps<T extends string> = {
  value: T;
  placeholder?: string;
  onChange: (value: T) => void;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  children: ReactNode;
  name?: string;
  buttonStyle?: CSSProperties;
  dropdownStyle?: CSSProperties;
};

export default function PulloverSelect<T extends string>({
  value,
  placeholder = 'Select...',
  onChange,
  align = 'start',
  disabled = false,
  children,
  name,
  buttonStyle,
  dropdownStyle,
}: PulloverSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Select.Root open={isOpen} value={value} onOpenChange={setIsOpen} onValueChange={onChange} disabled={disabled}>
      <Select.Trigger asChild>
        <UtilButton data-testid={`select-${name}-trigger`} iconRight="unfold_more" active={isOpen} style={buttonStyle}>
          <Select.Value placeholder={placeholder} />
        </UtilButton>
      </Select.Trigger>

      <Select.Portal>
        <div data-testid={`select-${name}-dropdown`} role="option" className={cn('pulloverSelectContentContainer')}>
          <Select.Content
            className={cn('content', 'scrollbar')}
            position="popper"
            sideOffset={8}
            align={align}
            avoidCollisions
            collisionPadding={8}
            style={dropdownStyle}
          >
            <Select.Viewport
              className={cn('viewport')}
              style={{
                overflow: 'unset',
              }}
            >
              {children}
            </Select.Viewport>
          </Select.Content>
        </div>
      </Select.Portal>
    </Select.Root>
  );
}

const Group: ForwardRefExoticComponent<ComponentProps<typeof Select.Group>> = forwardRef(
  ({ children, ...props }, ref) => {
    return (
      <Select.Group ref={ref} {...props} className={cn('group', props.className)}>
        {children}
        <Select.Separator className={cn('separator')} />
      </Select.Group>
    );
  },
);

Group.displayName = 'Group';

const GroupLabel: ForwardRefExoticComponent<ComponentProps<typeof Select.Label>> = forwardRef((props, ref) => {
  return <Select.Label ref={ref} {...props} className={cn('groupLabel', props.className)} />;
});

GroupLabel.displayName = 'GroupLabel';

const Option: ForwardRefExoticComponent<ComponentProps<typeof Select.Item>> = forwardRef(
  ({ children, ...props }, ref) => {
    return (
      <Select.Item ref={ref} {...props} className={cn('option', props.className)}>
        <div className={cn('indicatorContainer')}>
          <Select.ItemIndicator>
            <MaterialSymbol icon="check" size="20px" />
          </Select.ItemIndicator>
        </div>

        <Select.ItemText>{children}</Select.ItemText>
      </Select.Item>
    );
  },
);

Option.displayName = 'Option';

PulloverSelect.Option = Option;
PulloverSelect.Group = Group;
PulloverSelect.GroupLabel = GroupLabel;
